/**
 * EU-Gewährleistungshinweis (snippets/ewh-*.liquid)
 * - [data-ewh-open] and links to #gewaehrleistungshinweis (e.g. a footer menu item) open the popup #ewh-dialog;
 *   a page opened with that hash opens it right away
 * - The file is preloaded (idle after page load if a trigger exists, on hover / touch / focus of a trigger) and the
 *   popup waits briefly for the image to decode, so it never opens empty and the image does not pop in
 * - [data-ewh-close], a click on the backdrop and Escape close it (on phones after the bottom sheet slid down),
 *   focus returns to the trigger
 * - [data-ewh-scroll] scrolls to the notice in the cart (full mode)
 * - [data-ewh-tip] toggles the tooltip of the cart bar (tap on touch devices; hover is CSS)
 * Uses event delegation, because the cart drawer and cart page re-render via the Section Rendering API.
 */

(() => {
  if (window.ewhInitialized) return;
  window.ewhInitialized = true;

  const OPENER = '[data-ewh-open], a[href$="#gewaehrleistungshinweis"]';
  const SHEET_QUERY = '(max-width: 749px)';
  const HASH = '#gewaehrleistungshinweis';
  const DECODE_TIMEOUT = 600;

  let lastTrigger = null;
  let openedByPointer = false;
  let opening = false;
  let isOpen = false;

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const closeTips = (except) => {
    document.querySelectorAll('.ewh-tip.is-open').forEach((tip) => {
      if (tip === except) return;
      tip.classList.remove('is-open');
      document.querySelector(`[aria-describedby="${tip.id}"]`)?.setAttribute('aria-expanded', 'false');
    });
  };

  const loadFiles = () => {
    document.querySelectorAll('#ewh-dialog [data-ewh-src]').forEach((file) => {
      if (!file.getAttribute('src')) file.setAttribute('src', file.dataset.ewhSrc);
    });
  };

  // Resolves once the image is decoded, or after DECODE_TIMEOUT on a slow connection
  const waitForImage = (dialog) => {
    const image = dialog.querySelector('img.ewh-media__file');
    if (!image || (image.complete && image.naturalWidth)) return Promise.resolve();
    return Promise.race([image.decode().catch(() => {}), new Promise((resolve) => setTimeout(resolve, DECODE_TIMEOUT))]);
  };

  const openDialog = async (trigger) => {
    const dialog = document.getElementById('ewh-dialog');
    if (!dialog || dialog.open || opening) return;

    opening = true;
    loadFiles();
    await waitForImage(dialog);
    opening = false;

    lastTrigger = trigger;
    closeTips();
    document.documentElement.style.overflow = 'hidden';
    dialog.showModal();
    isOpen = true;
    dialog.querySelector('.ewh-dialog__close')?.focus();
  };

  // Runs once per close, right after dialog.close() and again from the close event (which a hidden tab may never get)
  const afterClose = (dialog) => {
    if (!isOpen) return;
    isOpen = false;

    dialog.classList.remove('is-closing');
    document.documentElement.style.removeProperty('overflow');

    if (window.location.hash === HASH) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    // Keyboard users get focus back on the trigger. After a mouse click or tap the dialog's own focus
    // restore would leave a focus ring on "Ansehen", so the focus is released instead.
    if (lastTrigger?.isConnected) {
      if (openedByPointer) {
        lastTrigger.blur();
      } else {
        lastTrigger.focus();
      }
    }
    lastTrigger = null;
  };

  // On phones the popup is a bottom sheet: let it slide down (CSS .is-closing) before it really closes
  const closeDialog = (dialog) => {
    if (!dialog.open || dialog.classList.contains('is-closing')) return;

    if (!window.matchMedia(SHEET_QUERY).matches || prefersReducedMotion()) {
      dialog.close();
      afterClose(dialog);
      return;
    }

    let closed = false;
    const onAnimationEnd = (event) => {
      if (event.target === dialog && !event.pseudoElement) finish();
    };
    const finish = () => {
      if (closed) return;
      closed = true;
      dialog.removeEventListener('animationend', onAnimationEnd);
      dialog.close();
      afterClose(dialog);
    };

    dialog.classList.add('is-closing');
    dialog.addEventListener('animationend', onAnimationEnd);
    // Fallback in case the animation does not run (e.g. hidden tab)
    setTimeout(finish, 400);
  };

  // Preload: on hover, touch or focus of a trigger …
  ['pointerover', 'touchstart', 'focusin'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        if (event.target.closest?.(OPENER)) loadFiles();
      },
      { passive: true }
    );
  });

  // … and on pages that show a trigger right away (product page), once the page is idle
  window.addEventListener('load', () => {
    if (!document.querySelector('[data-ewh-open]')) return;
    (window.requestIdleCallback || ((callback) => setTimeout(callback, 1500)))(loadFiles);
  });

  // A link to …#gewaehrleistungshinweis opens the popup on arrival
  if (window.location.hash === HASH) {
    openedByPointer = true;
    openDialog(null);
  }

  // Clicks inside the popup are handled on the popup and do not reach <body>: the cart drawer (MenuDrawer in
  // global.js) closes on every click outside of itself, and the popup sits outside of it at the end of <body>.
  // Links inside the popup still open as usual.
  document.getElementById('ewh-dialog')?.addEventListener('click', (event) => {
    event.stopPropagation();

    // Close via a close button or a click on the backdrop (the dialog element itself)
    const dialog = event.currentTarget;
    if (event.target === dialog || event.target.closest('[data-ewh-close]')) closeDialog(dialog);
  });

  document.addEventListener('click', (event) => {
    const target = event.target;

    const opener = target.closest(OPENER);
    if (opener && document.getElementById('ewh-dialog')) {
      event.preventDefault();
      // event.detail is 0 when the click comes from the keyboard (Enter or Space)
      openedByPointer = event.detail > 0;
      openDialog(opener);
      return;
    }

    const scrollLink = target.closest('[data-ewh-scroll]');
    if (scrollLink) {
      const notice = document.getElementById(scrollLink.getAttribute('href').slice(1));
      if (notice) {
        event.preventDefault();
        notice.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
        notice.focus({ preventScroll: true });
      }
      return;
    }

    const tipButton = target.closest('[data-ewh-tip]');
    if (tipButton) {
      const tip = document.getElementById(tipButton.getAttribute('aria-describedby'));
      if (tip) {
        const tipOpen = tip.classList.toggle('is-open');
        tipButton.setAttribute('aria-expanded', String(tipOpen));
        closeTips(tip);
      }
      return;
    }

    if (target.closest('[data-ewh-tip-close]') || !target.closest('.ewh-tip')) {
      closeTips();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeTips();
  });

  // Escape closes the popup on keydown and focus returns into the cart drawer, which closes itself on keyup
  // (MenuDrawer in global.js). Swallow that one keyup so the drawer stays open.
  let swallowEscapeKeyup = false;

  document.addEventListener(
    'cancel',
    (event) => {
      if (!event.target.matches?.('.ewh-dialog')) return;
      swallowEscapeKeyup = true;
      // Escape: close with the same slide-down as the close button
      event.preventDefault();
      closeDialog(event.target);
    },
    true
  );

  window.addEventListener(
    'keyup',
    (event) => {
      if (event.key !== 'Escape' || !swallowEscapeKeyup) return;
      swallowEscapeKeyup = false;
      event.stopImmediatePropagation();
    },
    true
  );

  // The close event does not bubble, so listen in the capture phase. It also covers closes the script does not start.
  document.addEventListener(
    'close',
    (event) => {
      if (event.target.matches?.('.ewh-dialog')) afterClose(event.target);
    },
    true
  );
})();
